import springboard from 'springboard';

import {databaseVersion} from '@/store/db/imported_migrations/imported_migrations';

import {OfficialWasmDialect} from 'kysely-wasm';

import {IndexedDbFileStorageProvider} from '@/modules/files/indexeddb_file_storage';
import {OPFSFileStorageProvider} from '@/modules/files/files_module_opfs';

import sqlite3InitModule from '@sqlite.org/sqlite-wasm';
import {isOpfsSupported} from '@/utils/user_agent';
import {BROWSER_FILE_UTILS} from '@/modules/files/remote_files/files_module_remote';
import {browserComputeSha1} from '@/utils/browser_hash_file';
import {FileClient, StoredFileMeta, StoredFileMetaWithDurationAndWaveformData} from '@/modules/files/files_module';
import {Selectors} from '@/store/selectors/selectors';
import {WithCallOptions} from '@/store/kysely_module';
import {generatePeaksFromArrayBuffer} from '@/utils/generate_waveform_data';
import {isAudioFile} from '@/utils/file_utils';
import {DBObjects} from '@/store/store_types';

export const createOfflineBrowserSqliteDialect = async () => {
    const sqlite3 = (await sqlite3InitModule({
        locateFile: () => `${location.origin}/public/sqlite3.wasm`
    })).oo1
    if (!sqlite3) {
        return Promise.reject('fail to load sqlite')
    }
    const path = `offline_db${databaseVersion}.db`;
    const db = sqlite3.OpfsDb
        ? new sqlite3.OpfsDb(path)
        : new sqlite3.JsStorageDb('local');

    return new OfficialWasmDialect({
        database: db as any,
    });
};

export const createOfflineBrowserFileClient = async (selectors: Selectors): Promise<FileClient> => {
    const indexDBFileProvider = new IndexedDbFileStorageProvider();
    await indexDBFileProvider.initialize();

    const opfsFileProvider = new OPFSFileStorageProvider();
    const opfsSupported = isOpfsSupported();
    if (opfsSupported) {
        await opfsFileProvider.initialize();
    }

    const uploadFile = async (file: File, workspaceId: string): Promise<StoredFileMeta> => {
        if (opfsSupported) {
            const fileShort = await opfsFileProvider.uploadFile(file);
            const {hash, size} = await browserComputeSha1(file);
            return {
                name: file.name,
                fullPath: `opfs://${fileShort.id.toString()}`,
                mimeType: file.type,
                size,
                hash,
            };
        }

        const fileShort = await indexDBFileProvider.uploadFile(file);
        const {hash, size} = await browserComputeSha1(file);
        return {
            name: file.name,
            fullPath: `idb://${fileShort.id.toString()}`,
            mimeType: file.type,
            size,
            hash,
        };
    };

    return {
        chooseFileAndUpload: async (workspaceId, callbacks) => {
            const fileOrFiles = await BROWSER_FILE_UTILS.chooseFileInBrowser({shouldUseDirectory: callbacks.shouldUseDirectory});
            if (!fileOrFiles) {
                return null;
            }

            // TODO: correctly handle directories for offline browser
            const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;

            callbacks.onUploadStart(file.name, {avoidToast: true});

            let duration: number | null = null;
            let waveform_data: string | null = null;

            if (isAudioFile(file.name)) {
                const peaksAndDuration = await generatePeaksFromArrayBuffer(await file.arrayBuffer(), 1000, (arrayBuffer) => {
                    const audioContext = new (globalThis.AudioContext || (globalThis as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext)();
                    return audioContext.decodeAudioData(arrayBuffer);
                });
                duration = peaksAndDuration.duration;
                waveform_data = JSON.stringify(peaksAndDuration.peaks);
            }

            const fileMeta = await uploadFile(file, workspaceId);
            return {
                ...fileMeta,
                duration,
                waveform_data,
            };
        },
        // uploadFile,
        getFileSrc: async (file) => {
            const fileInfo = await selectors.getFileInfoWithRaw({file_id: file.id}, {mode: 'local'});
            if (!fileInfo) {
                return ''; // TODO: handle this error case
            }

            if (fileInfo.full_path.startsWith('opfs://')) {
                return opfsFileProvider.getFileContent(fileInfo.full_path.substring('opfs://'.length));
            }

            const filePath = fileInfo.full_path.startsWith('idb://') ? fileInfo.full_path.substring('idb://'.length) : fileInfo.full_path;

            return indexDBFileProvider.getFileContent(filePath);
        },
    };
};
